import { useToast } from '@folio/components';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
  BrokerageFeeOnPolicy,
  CalculatePolicyQuoteInputBody,
  CalculatePolicyQuoteOutput,
  ConfigQuoteChosenWarrantyDto,
  CustomFieldDto,
  Duration,
  Insurer,
  ManualQuoteComponentDto,
  ManualQuotePercentageDto,
  QuoteFailedErrorDto,
  configControllerGetPublicProductConfigQueryOptions,
  policyControllerCalculatePolicyQuote,
  policyControllerCalculatePolicyQuoteQueryKey,
} from 'korint-sdk';
import { useDebouncedValue } from 'korint-sdk/react';
import { get } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PriceBreakdown } from './use-available-pricing';

const EMPTY_PERILS: ConfigQuoteChosenWarrantyDto[] = [];

export function withTierRequiredPerils(
  chosenPerils: ConfigQuoteChosenWarrantyDto[],
  tierPerils: { id: string; required?: boolean; options?: string[] }[],
): ConfigQuoteChosenWarrantyDto[] {
  const tierAvailablePerils = tierPerils.map(({ id }) => id);

  const correctedPerils = chosenPerils.filter(({ id }) => tierAvailablePerils.includes(id));

  for (const { id, required, options } of tierPerils) {
    if (!required) {
      continue;
    }

    const chosenPerilIndex = correctedPerils.findIndex(p => p.id === id);
    const chosenPeril = correctedPerils[chosenPerilIndex];

    if (!chosenPeril) {
      if (options) {
        correctedPerils.push({ id, option: options[0] });
      } else {
        correctedPerils.push({ id });
      }
    } else {
      if (options) {
        if (!chosenPeril.option || !options.includes(chosenPeril.option)) {
          correctedPerils[chosenPerilIndex] = { id, option: options[0] };
        }
      } else {
        if (chosenPeril.option) {
          correctedPerils.splice(chosenPerilIndex, 1);
          correctedPerils.push({ id });
        }
      }
    }
  }

  return correctedPerils;
}

/**
 * @debt see what we do for multiple assets / fleet
 */
export function useCalculateQuotes({
  productId,
  policyId,
  branchId,
  assetIds,
  chosenPerils = EMPTY_PERILS,
  brokerageFeesOverride,
  periodDurationOverride,
  customFieldsToOverride,
  additionalQuoteComponents,
  additionalQuotePercentages,
  prorated,
  insurers,
}: {
  productId?: string;
  policyId: string;
  branchId?: string;
  assetIds: string[];
  chosenPerils?: ConfigQuoteChosenWarrantyDto[];
  brokerageFeesOverride?: BrokerageFeeOnPolicy[];
  periodDurationOverride?: Duration;
  customFieldsToOverride?: CustomFieldDto[];
  additionalQuoteComponents?: ManualQuoteComponentDto[];
  additionalQuotePercentages?: ManualQuotePercentageDto[];
  prorated?: boolean;
  insurers?: Insurer[];
}) {
  const toast = useToast();
  const { t } = useTranslation();

  const { data: quoteConfig } = useQuery({
    ...configControllerGetPublicProductConfigQueryOptions(productId!),
    select: data => data.quote,
    enabled: Boolean(productId),
  });

  // Debounce the inputs to avoid excessive API requests
  const debouncedChosenPerils = useDebouncedValue(chosenPerils, 300);
  const debouncedBrokerageFeesOverride = useDebouncedValue(brokerageFeesOverride, 300);
  const debouncedPeriodDurationOverride = useDebouncedValue(periodDurationOverride, 300);
  const debouncedCustomFieldsToOverride = useDebouncedValue(customFieldsToOverride, 300);
  const debouncedAdditionalQuoteComponents = useDebouncedValue(additionalQuoteComponents, 300);
  const debouncedAdditionalQuotePercentages = useDebouncedValue(additionalQuotePercentages, 300);

  const configsToCalculateQuoteFor = (insurers ?? [undefined]).flatMap(insurer =>
    (quoteConfig?.availableConfigs ?? []).map(({ tier, excess, perils = [] }) => {
      const correctedPerils = withTierRequiredPerils(debouncedChosenPerils, perils);

      return { insurer, tier, excess, perils: correctedPerils };
    }),
  );

  type QuoteResult = {
    insurer?: Insurer;
    tier: string;
    excess: string;
    quote: CalculatePolicyQuoteOutput | null;
    breakdown?: PriceBreakdown;
    error?: QuoteFailedErrorDto;
    invalidPricingDetailsUserError?: { title: string; description: string };
  };

  const [previousData, setPreviousData] = useState<QuoteResult[]>([]);
  const toastFiredRef = useRef(false);

  const queriesResult = useQueries({
    queries: configsToCalculateQuoteFor.map(({ insurer, tier, excess, perils }) => ({
      enabled: quoteConfig?.availableConfigs !== undefined && assetIds.length > 0,
      queryKey: [
        // Prefix the query key to avoid collisions, with the calculateQuote in QuoteFunnelModule for example
        'useCalculateQuotes',
        ...policyControllerCalculatePolicyQuoteQueryKey(
          policyId,
          {
            assetIds,
            insurer,
            options: assetIds.map(assetId => ({ assetId, tier, excess, perils })),
            brokerageFeesOverride: debouncedBrokerageFeesOverride,
            customFieldsToOverride: debouncedCustomFieldsToOverride,
            periodDurationOverride: debouncedPeriodDurationOverride,
            additionalQuoteComponentsAndPercentages: {
              components: debouncedAdditionalQuoteComponents ?? [],
              percentages: debouncedAdditionalQuotePercentages ?? [],
            },
            prorated,
          },
          { branchId },
        ),
      ],
      queryFn: async (): Promise<QuoteResult> => {
        try {
          const input: CalculatePolicyQuoteInputBody = {
            assetIds,
            insurer,
            options: assetIds.map(assetId => ({ assetId, tier, excess, perils })),
            brokerageFeesOverride: debouncedBrokerageFeesOverride,
            customFieldsToOverride: debouncedCustomFieldsToOverride,
            periodDurationOverride: debouncedPeriodDurationOverride,
            additionalQuoteComponentsAndPercentages: {
              components: debouncedAdditionalQuoteComponents ?? [],
              percentages: debouncedAdditionalQuotePercentages ?? [],
            },
            prorated,
          };

          const quote: CalculatePolicyQuoteOutput = await policyControllerCalculatePolicyQuote(
            policyId,
            input,
            { branchId },
          );

          return { insurer, tier, excess, quote, breakdown: quote?.breakdown };
        } catch (e) {
          const errorCode = get(e, 'errorResponse.data.code') as string | undefined;

          if (errorCode === 'QUOTE_FAILED' || errorCode === 'WAKAM_API_VALIDATION_ERROR') {
            const error = get(e, 'errorResponse.data') as unknown as QuoteFailedErrorDto;

            if (error.details.failedReason === 'NEGATIVE_PREMIUM') {
              toast.toast({
                title: 'Montant de prime négatif',
                description:
                  'Vérifiez que les commissions incluses ne sont pas supérieures au montant de la prime',
              });
            }

            return { insurer, tier, excess, quote: null, error };
          }

          if (errorCode === 'INVALID_PRICING_DETAILS') {
            const error = get(e, 'errorResponse.data') as unknown as QuoteFailedErrorDto;
            const title = get(e, 'userError') as string | undefined;
            const description = get(e, 'userHelp') as string | undefined;
            const invalidPricingDetailsUserError = title
              ? { title, description: description ?? '' }
              : undefined;
            return { insurer, tier, excess, quote: null, error, invalidPricingDetailsUserError };
          }

          if (
            errorCode === 'INVALID_BROKERAGE_FEE' ||
            errorCode === 'INVALID_TOTAL_BROKERAGE_FEE'
          ) {
            const { min, max } = get(e, 'errorResponse.data.details', {}) as {
              min?: number;
              max?: number;
            };

            return {
              insurer,
              tier,
              excess,
              quote: null,
              invalidPricingDetailsUserError: {
                title: t('quoteFunnel.commissionOutOfBounds.title'),
                description: t(
                  errorCode === 'INVALID_TOTAL_BROKERAGE_FEE'
                    ? 'quoteFunnel.commissionOutOfBounds.descriptionTotal'
                    : 'quoteFunnel.commissionOutOfBounds.description',
                  { min, max },
                ),
              },
            };
          }

          throw e;
        }
      },
      retry: 0,
    })),
    combine: results => ({
      isLoading: results.some(result => result.isPending),
      totalCount: results.length,
      data: results.map(result => result.data).filter(Boolean) as QuoteResult[],
    }),
  });

  useEffect(() => {
    if (queriesResult.isLoading) {
      toastFiredRef.current = false;
      return;
    }
    if (queriesResult.data.length > 0) {
      const allSettledWithData = queriesResult.data.length === queriesResult.totalCount;
      const allFailed = allSettledWithData && queriesResult.data.every(d => d.quote === null);
      if (allFailed && !toastFiredRef.current) {
        const noValidPricingError = queriesResult.data.find(
          d => d.invalidPricingDetailsUserError,
        )?.invalidPricingDetailsUserError;
        if (noValidPricingError) {
          toastFiredRef.current = true;
          toast.toast({
            title: noValidPricingError.title,
            description: noValidPricingError.description,
          });
        }
      }
      setPreviousData(queriesResult.data);
    }
  }, [queriesResult]);

  return {
    // Mimick keepPreviousData behavior: only retain initial loading to avoid components flickering
    isLoading: queriesResult.isLoading && !previousData.length,
    data: queriesResult.isLoading ? previousData : queriesResult.data,
  };
}
